// Subscribe to a run's SSE event stream and invalidate the run-related
// react-query caches on each event — so the UI sees per-node updates
// land near-instantly instead of waiting for the next polling tick.
// (DEV-16173 followup.)
//
// Both the standalone `/triage/runs/:id` page and the workflow builder's
// `InlineRunDetail` use this. The only thing it owns is the EventSource
// lifecycle + cache invalidation; the components still own their own
// `useQuery` calls (polling remains a belt-and-suspenders fallback when
// EventSource construction fails or the connection drops).
import { fork } from '@cortex/utils/fork';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useAuth } from '../auth/AuthProvider';

import { subscribeRunEvents } from './sse';

/**
 * Open one SSE subscription for `runId` while `isActive` is true; close
 * it when `isActive` flips false or the component unmounts. Each event
 * invalidates the standard run query keys so the next render re-fetches
 * fresh data.
 *
 * Pass `runId: null` to no-op (the hook still runs, just doesn't subscribe).
 */
export function useRunEventStreamInvalidation(runId: string | null, isActive: boolean): void {
  const qc = useQueryClient();
  const { token } = useAuth();

  useEffect(() => {
    if (!runId || !isActive) return;
    const baseUrl = (
      import.meta.env.VITE_API_URL ?? 'https://api.cortex-dev.formant.ai/api'
    ).replace(/\/$/, '');
    return subscribeRunEvents(baseUrl, runId, token, () => {
      fork(
        (async () => {
          // Every query key any consumer caches off of this run goes
          // here. Cheap to over-invalidate — react-query dedupes
          // in-flight refetches by key.
          for (const queryKey of [
            ['run', runId] as const,
            ['run-node-executions', runId, 'trace'] as const,
            ['run', runId, 'costs'] as const,
            ['live-run', runId] as const,
            ['live-run', runId, 'node-executions'] as const,
          ]) {
            try {
              await qc.invalidateQueries({ queryKey });
            } catch (err) {
              console.error(err);
            }
          }
        })(),
      );
    });
  }, [runId, isActive, qc, token]);
}
